import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import useJwt from '@src/auth/jwt/useJwt'

// Async thunks
export const fetchTickets = createAsyncThunk(
  'tickets/fetchTickets',
  async (params, { rejectWithValue }) => {
    try {
      const response = await useJwt.getTickets(params)
      return response.data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const fetchTicket = createAsyncThunk(
  'tickets/fetchTicket',
  async (id, { rejectWithValue }) => {
    try {
      const response = await useJwt.getTicket(id)
      return response.data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const createTicket = createAsyncThunk(
  'tickets/createTicket',
  async (ticketData, { rejectWithValue }) => {
    try {
      const response = await useJwt.createTicket(ticketData)
      return response.data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const updateTicket = createAsyncThunk(
  'tickets/updateTicket',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await useJwt.updateTicket(id, data)
      return response.data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const deleteTicket = createAsyncThunk(
  'tickets/deleteTicket',
  async (id, { rejectWithValue }) => {
    try {
      await useJwt.deleteTicket(id)
      return id
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const addComment = createAsyncThunk(
  'tickets/addComment',
  async ({ ticketId, commentData }, { rejectWithValue }) => {
    try {
      const response = await useJwt.addTicketComment(ticketId, commentData)
      return response.data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const fetchMetadata = createAsyncThunk(
  'tickets/fetchMetadata',
  async (_, { rejectWithValue }) => {
    try {
      const response = await useJwt.getTicketsMetadata()
      return response.data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

const initialState = {
  tickets: [],
  currentTicket: null,
  metadata: {
    statuses: [],
    priorities: [],
    categories: [],
    agents: []
  },
  pagination: {
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1
  },
  filters: {
    search: '',
    status: '',
    priority: '',
    category: '',
    assigned_to: ''
  },
  loading: {
    tickets: false,
    currentTicket: false,
    metadata: false,
    creating: false,
    updating: false,
    deleting: false,
    commenting: false
  },
  error: null
}

const ticketsSlice = createSlice({
  name: 'tickets',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload }
      state.pagination.current_page = 1 // Reset to first page when filters change
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload }
    },
    clearCurrentTicket: (state) => {
      state.currentTicket = null
    },
    clearError: (state) => {
      state.error = null
    },
    updateTicketInList: (state, action) => {
      const index = state.tickets.findIndex(ticket => ticket.id === action.payload.id)
      if (index !== -1) {
        state.tickets[index] = { ...state.tickets[index], ...action.payload }
      }
    },
    addCommentToCurrentTicket: (state, action) => {
      if (state.currentTicket) {
        state.currentTicket.comments = state.currentTicket.comments || []
        state.currentTicket.comments.push(action.payload)
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch tickets
      .addCase(fetchTickets.pending, (state) => {
        state.loading.tickets = true
        state.error = null
      })
      .addCase(fetchTickets.fulfilled, (state, action) => {
        state.loading.tickets = false
        if (action.payload.success) {
          state.tickets = action.payload.data.data
          state.pagination = {
            current_page: action.payload.data.current_page,
            per_page: action.payload.data.per_page,
            total: action.payload.data.total,
            last_page: action.payload.data.last_page
          }
        }
      })
      .addCase(fetchTickets.rejected, (state, action) => {
        state.loading.tickets = false
        state.error = action.payload
      })

      // Fetch single ticket
      .addCase(fetchTicket.pending, (state) => {
        state.loading.currentTicket = true
        state.error = null
      })
      .addCase(fetchTicket.fulfilled, (state, action) => {
        state.loading.currentTicket = false
        if (action.payload.success) {
          state.currentTicket = action.payload.data
        }
      })
      .addCase(fetchTicket.rejected, (state, action) => {
        state.loading.currentTicket = false
        state.error = action.payload
      })

      // Create ticket
      .addCase(createTicket.pending, (state) => {
        state.loading.creating = true
        state.error = null
      })
      .addCase(createTicket.fulfilled, (state, action) => {
        state.loading.creating = false
        if (action.payload.success) {
          state.tickets.unshift(action.payload.data)
          state.currentTicket = action.payload.data
        }
      })
      .addCase(createTicket.rejected, (state, action) => {
        state.loading.creating = false
        state.error = action.payload
      })

      // Update ticket
      .addCase(updateTicket.pending, (state) => {
        state.loading.updating = true
        state.error = null
      })
      .addCase(updateTicket.fulfilled, (state, action) => {
        state.loading.updating = false
        if (action.payload.success) {
          // Update in tickets list
          const index = state.tickets.findIndex(ticket => ticket.id === action.payload.data.id)
          if (index !== -1) {
            state.tickets[index] = action.payload.data
          }
          // Update current ticket if it's the same
          if (state.currentTicket && state.currentTicket.id === action.payload.data.id) {
            state.currentTicket = { ...state.currentTicket, ...action.payload.data }
          }
        }
      })
      .addCase(updateTicket.rejected, (state, action) => {
        state.loading.updating = false
        state.error = action.payload
      })

      // Delete ticket
      .addCase(deleteTicket.pending, (state) => {
        state.loading.deleting = true
        state.error = null
      })
      .addCase(deleteTicket.fulfilled, (state, action) => {
        state.loading.deleting = false
        if (action.payload.success) {
          state.tickets = state.tickets.filter(ticket => ticket.id !== action.payload.id)
          if (state.currentTicket && state.currentTicket.id === action.payload.id) {
            state.currentTicket = null
          }
        }
      })
      .addCase(deleteTicket.rejected, (state, action) => {
        state.loading.deleting = false
        state.error = action.payload
      })

      // Add comment
      .addCase(addComment.pending, (state) => {
        state.loading.commenting = true
        state.error = null
      })
      .addCase(addComment.fulfilled, (state, action) => {
        state.loading.commenting = false
        if (action.payload.success && state.currentTicket) {
          state.currentTicket.comments = state.currentTicket.comments || []
          state.currentTicket.comments.push(action.payload.data)
        }
      })
      .addCase(addComment.rejected, (state, action) => {
        state.loading.commenting = false
        state.error = action.payload
      })

      // Fetch metadata
      .addCase(fetchMetadata.pending, (state) => {
        state.loading.metadata = true
        state.error = null
      })
      .addCase(fetchMetadata.fulfilled, (state, action) => {
        state.loading.metadata = false
        if (action.payload.success) {
          state.metadata = action.payload.data
        }
      })
      .addCase(fetchMetadata.rejected, (state, action) => {
        state.loading.metadata = false
        state.error = action.payload
      })
  }
})

export const {
  setFilters,
  setPagination,
  clearCurrentTicket,
  clearError,
  updateTicketInList,
  addCommentToCurrentTicket
} = ticketsSlice.actions

export default ticketsSlice.reducer
